import React, { useEffect, useState } from 'react';
import { 
    ArrowLeft, 
    User, 
    Building, 
    Calendar, 
    Clock, 
    Phone,
    MessageSquare,
    CheckCircle,
    XCircle,
    AlertCircle,
    Loader2,
    Mail
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';

interface BookingDetail {
    id: string;
    requesterId: string;
    requesterName: string;
    requesterAvatar: string | null;
    requesterPhone: string | null;
    requesterEmail: string | null;
    propertyId: string;
    propertyName: string;
    unitName: string;
    requestedDate: string;
    requestedTime: string;
    status: 'Pending' | 'Confirmed' | 'Cancelled';
    notes?: string;
}

export const BookingDetails: React.FC = () => {
    const { bookingId } = useParams<{ bookingId: string }>();
    const navigate = useNavigate();
    const { warning, success } = useToast();
    const [booking, setBooking] = useState<BookingDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        const fetchBookingDetail = async () => {
            setLoading(true);
            try {
                // Simulate API call
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // Mock data - replace with actual API call
                const mockData: BookingDetail = {
                    id: bookingId || '',
                    requesterId: 'user_123',
                    requesterName: 'Sarah Mwangi',
                    requesterAvatar: `https://ui-avatars.com/api/?name=Sarah+Mwangi&background=random`,
                    requesterPhone: '+254 722 123 456',
                    requesterEmail: 'sarah.mwangi@example.com',
                    propertyId: 'prop_456',
                    propertyName: 'Majengo Heights',
                    unitName: '2 Bedroom Apartment - Unit B203',
                    requestedDate: '2026-05-20',
                    requestedTime: '10:00 AM',
                    status: 'Pending',
                    notes: 'Interested in long-term lease. Would like to see the kitchen and balcony.'
                };
                
                setBooking(mockData);
            } catch (error) {
                warning('Error', 'Failed to load booking details');
            } finally {
                setLoading(false);
            }
        };

        if (bookingId) {
            fetchBookingDetail();
        }
    }, [bookingId, warning]);

    const handleUpdateStatus = async (newStatus: 'Confirmed' | 'Cancelled') => {
        if (!booking) return;
        
        setActionLoading(true);
        
        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 800));
            setBooking({ ...booking, status: newStatus });
            success('Success', `Booking ${newStatus.toLowerCase()} successfully`);
        } catch (error) {
            warning('Error', 'Failed to update booking status');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCall = () => {
        if (booking?.requesterPhone) {
            window.location.href = `tel:${booking.requesterPhone}`;
        }
    };

    const handleMessage = () => {
        if (booking?.requesterEmail) {
            window.location.href = `mailto:${booking.requesterEmail}`;
        }
    };

    const getStatusStyle = (status: string) => {
        switch (status) {
            case "Confirmed":
                return { text: "text-green-600 dark:text-green-400", bg: "bg-green-100 dark:bg-green-900/30" };
            case "Cancelled":
                return { text: "text-red-600 dark:text-red-400", bg: "bg-red-100 dark:bg-red-900/30" };
            case "Pending":
            default:
                return { text: "text-yellow-600 dark:text-yellow-400", bg: "bg-yellow-100 dark:bg-yellow-900/30" };
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <Loader2 className="animate-spin text-primary w-10 h-10" />
                <p className="mt-4 text-light-subtle dark:text-dark-subtle">Loading booking details...</p>
            </div>
        );
    }

    if (!booking) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
                <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
                <h2 className="text-xl font-bold text-light-text dark:text-dark-text">Booking Request Not Found</h2>
                <p className="mt-2 text-light-subtle dark:text-dark-subtle">The requested booking could not be found.</p>
                <button
                    onClick={() => navigate(-1)}
                    className="mt-6 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                    Go Back
                </button>
            </div>
        );
    }

    const statusStyle = getStatusStyle(booking.status);

    return (
        <div className="space-y-6 pb-12 min-h-[80vh]">
            {/* Header */}
            <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 border-b border-light-border dark:border-dark-border">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                >
                    <ArrowLeft size={24} className="text-primary dark:text-golden" />
                </button>
                <div>
                    <h1 className="text-xl font-bold text-light-text dark:text-dark-text">Viewing Request</h1>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Request #{booking.id.substring(0, 8)}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 px-4">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Requester Info Card */}
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4 flex items-center gap-2">
                            <User size={20} className="text-primary dark:text-golden" />
                            Request From
                        </h3>
                        <div className="flex items-center gap-4">
                            <img
                                src={booking.requesterAvatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(booking.requesterName)}&background=random`}
                                alt={booking.requesterName}
                                className="w-16 h-16 rounded-full"
                            />
                            <div className="flex-1">
                                <h4 className="text-lg font-bold text-light-text dark:text-dark-text">{booking.requesterName}</h4>
                                <p className="text-light-subtle dark:text-dark-subtle">has requested a viewing.</p>
                                <div className="flex gap-2 mt-3">
                                    <button
                                        onClick={handleCall}
                                        className="flex items-center gap-1 px-3 py-1.5 bg-light-bg dark:bg-dark-bg rounded-lg text-sm font-medium text-primary dark:text-golden hover:bg-light-bg/80 dark:hover:bg-dark-bg/80 transition-colors"
                                    >
                                        <Phone size={14} />
                                        Call
                                    </button>
                                    <button
                                        onClick={handleMessage}
                                        className="flex items-center gap-1 px-3 py-1.5 bg-light-bg dark:bg-dark-bg rounded-lg text-sm font-medium text-primary dark:text-golden hover:bg-light-bg/80 dark:hover:bg-dark-bg/80 transition-colors"
                                    >
                                        <Mail size={14} />
                                        Email
                                    </button>
                                    <button
                                        onClick={handleMessage}
                                        className="flex items-center gap-1 px-3 py-1.5 bg-light-bg dark:bg-dark-bg rounded-lg text-sm font-medium text-primary dark:text-golden hover:bg-light-bg/80 dark:hover:bg-dark-bg/80 transition-colors"
                                    >
                                        <MessageSquare size={14} />
                                        Message
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Booking Details Card */}
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4 flex items-center gap-2">
                            <Building size={20} className="text-primary dark:text-golden" />
                            Property & Unit
                        </h3>
                        <div className="space-y-4">
                            <div className="flex items-start gap-3 p-4 bg-light-bg dark:bg-dark-bg rounded-lg">
                                <Building size={20} className="text-light-subtle dark:text-dark-subtle mt-0.5" />
                                <div>
                                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Property</p>
                                    <p className="text-base font-semibold text-light-text dark:text-dark-text">{booking.propertyName}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-4 bg-light-bg dark:bg-dark-bg rounded-lg">
                                <Calendar size={20} className="text-light-subtle dark:text-dark-subtle mt-0.5" />
                                <div>
                                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Unit</p>
                                    <p className="text-base font-semibold text-light-text dark:text-dark-text">{booking.unitName}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-4 bg-light-bg dark:bg-dark-bg rounded-lg">
                                <Clock size={20} className="text-light-subtle dark:text-dark-subtle mt-0.5" />
                                <div>
                                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Requested Date & Time</p>
                                    <p className="text-base font-semibold text-light-text dark:text-dark-text">
                                        {new Date(booking.requestedDate).toLocaleDateString('en-US', {
                                            weekday: 'long',
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </p>
                                    <p className="text-lg font-bold text-primary dark:text-golden mt-1">{booking.requestedTime}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <p className="text-sm text-light-subtle dark:text-dark-subtle">Status</p>
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                                    {booking.status}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Notes */}
                    {booking.notes && (
                        <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                            <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4 flex items-center gap-2">
                                <MessageSquare size={20} className="text-primary dark:text-golden" />
                                Notes from Requester
                            </h3>
                            <p className="text-light-text dark:text-dark-text leading-relaxed">{booking.notes}</p>
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Quick Actions */}
                    {booking.status === 'Pending' && (
                        <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                            <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4">Actions</h3>
                            <div className="space-y-3">
                                <button
                                    onClick={() => handleUpdateStatus('Confirmed')}
                                    disabled={actionLoading}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                >
                                    {actionLoading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                                    Confirm Booking
                                </button>
                                <button
                                    onClick={() => handleUpdateStatus('Cancelled')}
                                    disabled={actionLoading}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                >
                                    <XCircle size={18} />
                                    Decline Request
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Booking Info */}
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4">Booking Information</h3>
                        <div className="space-y-4">
                            <div>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle">Request ID</p>
                                <p className="text-sm font-medium text-light-text dark:text-dark-text font-mono">
                                    {booking.id}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle">Requester ID</p>
                                <p className="text-sm font-medium text-light-text dark:text-dark-text font-mono">
                                    {booking.requesterId}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle">Property ID</p>
                                <p className="text-sm font-medium text-light-text dark:text-dark-text font-mono">
                                    {booking.propertyId}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Contact Info */}
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4">Contact Information</h3>
                        <div className="space-y-3">
                            {booking.requesterPhone && (
                                <div className="flex items-center gap-3">
                                    <Phone size={16} className="text-light-subtle dark:text-dark-subtle" />
                                    <span className="text-sm text-light-text dark:text-dark-text">{booking.requesterPhone}</span>
                                </div>
                            )}
                            {booking.requesterEmail && (
                                <div className="flex items-center gap-3">
                                    <Mail size={16} className="text-light-subtle dark:text-dark-subtle" />
                                    <span className="text-sm text-light-text dark:text-dark-text">{booking.requesterEmail}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};